import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
  GlobalStyles,
  styled,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Swal from "sweetalert2";
import { useTheme } from "@emotion/react";
import { UploadStatement } from "../../../data/DynamicTable/DynamicFunctions";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { customerservice } from "../../../config";
import { tokens } from "../../../theme";

const BRAND_BLUE = "#1766A2";
const BRAND_BLUE_BRIGHT = "#2e9be6";
const NEON_GREEN = "#7ad9aa";

const validationSchema = yup.object().shape({
  file: yup
    .mixed()
    .required("Please select a file to upload!")
    .test("fileSize", "The file has exceeded the 50mb limit", (value) => {
      return value && value.size <= 52428800; // 50MB
    }),
  password: yup.string().required("Please enter statement password"),
});

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Styled building blocks
// ---------------------------------------------------------------------------
const GlassCard = styled(Card)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    borderRadius: "16px",
    border: `1px solid ${
      isDark ? "rgba(46,155,230,0.25)" : "rgba(23,102,162,0.18)"
    }`,
    background: isDark
      ? "linear-gradient(145deg, rgba(23,102,162,0.14), rgba(12,28,44,0.9))"
      : "linear-gradient(145deg, rgba(23,102,162,0.05), #ffffff)",
    backdropFilter: "blur(6px)",
    transition: "box-shadow 0.25s ease",
    "&:hover": {
      boxShadow: isDark
        ? "0 0 20px rgba(46,155,230,0.22)"
        : "0 8px 24px rgba(23,102,162,0.15)",
    },
  };
});

const FuturisticTextField = styled(TextField)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  const base = {
    borderRadius: "12px",
    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(23,102,162,0.04)",
    transition: "box-shadow 0.25s ease",
    "&:hover": {
      backgroundColor: isDark
        ? "rgba(255,255,255,0.06)"
        : "rgba(23,102,162,0.06)",
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${BRAND_BLUE_BRIGHT}33, 0 0 14px ${BRAND_BLUE_BRIGHT}44`,
    },
  };
  return {
    "& .MuiFilledInput-root": {
      ...base,
      "&:before, &:after": { display: "none" }, // remove filled underline
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": { color: BRAND_BLUE_BRIGHT },
    },
  };
});

// This is the adhoc (company-wide) counterpart to the per-customer
// UploadStatementForm used on a customer's Mpesa Statements tab — since
// there's no customerId in context here, it adds a customer search step
// before the same UploadStatement upload flow.
const UploadFinsightsStatementForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setCustomer(null);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetchJsonDynamic(
          "dynamicCustomerSearch",
          "customer",
          { searchTerm: searchTerm.trim(), approval: 2, isAssetCustomer: 1 },
          customerservice.uri
        );
        const found = response?.results ? JSON.parse(response.results) : null;
        setCustomer(found && found.Id ? found : null);
      } catch (error) {
        setCustomer(null);
      }
      setSearching(false);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const resetCustomer = () => {
    setCustomer(null);
    setSearchTerm("");
  };

  const initialValues = {};

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const enhancedValues = { ...values, customerId: customer.Id };
      const response = await UploadStatement(enhancedValues);

      if (response === 200) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Statement uploaded successfully for ${customer.FirstName} ${customer.OtherNames}.`,
        });
      }

      setSubmitting(false);
      resetForm();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.error || "An unexpected error occurred.",
      });
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px" display="flex" flexDirection="column" gap={2.5}>
      <GlobalStyles
        styles={{
          "@keyframes fsFadeUp": {
            "0%": { opacity: 0, transform: "translateY(12px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      />

      {/* ---------- Header ---------- */}
      <Box sx={{ animation: "fsFadeUp 0.4s ease both" }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: `linear-gradient(90deg, ${BRAND_BLUE} 20%, ${BRAND_BLUE_BRIGHT} 70%, ${NEON_GREEN})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Upload Statement
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search for a customer, then upload their Mpesa or bank statement to
          be crunched
        </Typography>
      </Box>

      {/* ---------- Step 1: customer search ---------- */}
      <GlassCard sx={{ animation: "fsFadeUp 0.45s ease 0.05s both" }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.72rem",
                fontWeight: 800,
                color: "#fff",
                background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_BLUE_BRIGHT})`,
                boxShadow: `0 0 10px ${BRAND_BLUE_BRIGHT}55`,
              }}
            >
              1
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Find the customer
            </Typography>
          </Box>

          <FuturisticTextField
            fullWidth
            variant="filled"
            label="Search Customer (Name, Phone or ID number)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={!!customer}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? (
                    <CircularProgress size={16} sx={{ color: BRAND_BLUE_BRIGHT }} />
                  ) : (
                    <SearchIcon fontSize="small" sx={{ color: BRAND_BLUE_BRIGHT }} />
                  )}
                </InputAdornment>
              ),
            }}
          />

          {!customer && !searching && searchTerm.trim().length >= 3 && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mt={1.5}
              sx={{ color: "#e06666" }}
            >
              <ErrorOutlineIcon fontSize="small" />
              <Typography variant="body2">
                No approved customer found matching "{searchTerm}".
              </Typography>
            </Box>
          )}
        </CardContent>
      </GlassCard>

      {/* ---------- Step 2: selected customer ---------- */}
      {customer && (
        <GlassCard sx={{ animation: "fsFadeUp 0.45s ease 0.1s both" }}>
          <CardContent sx={{ p: "16px !important" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: NEON_GREEN,
                    bgcolor: `${NEON_GREEN}1a`,
                    border: `1px solid ${NEON_GREEN}55`,
                    boxShadow: `0 0 12px ${NEON_GREEN}33`,
                  }}
                >
                  <PersonOutlineIcon />
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {customer.FirstName} {customer.OtherNames}
                    </Typography>
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      sx={{ color: NEON_GREEN }}
                    />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={2} mt={0.5}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <PhoneIphoneOutlinedIcon sx={{ fontSize: 14 }} />
                      {customer.MobileNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <BadgeOutlinedIcon sx={{ fontSize: 14 }} />
                      {customer.IdNoOrPassport || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Tooltip title="Search a different customer">
                <IconButton
                  onClick={resetCustomer}
                  size="small"
                  sx={{
                    color: BRAND_BLUE_BRIGHT,
                    "&:hover": { rotate: "-90deg" },
                    transition: "rotate 0.3s",
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </GlassCard>
      )}

      {/* ---------- Step 3: upload ---------- */}
      {customer && (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <GlassCard sx={{ animation: "fsFadeUp 0.45s ease 0.15s both" }}>
                <CardContent sx={{ p: "16px !important" }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: "#fff",
                        background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_BLUE_BRIGHT})`,
                        boxShadow: `0 0 10px ${BRAND_BLUE_BRIGHT}55`,
                      }}
                    >
                      2
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Upload the statement
                    </Typography>
                  </Box>

                  <Box
                    display="grid"
                    gap="20px"
                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                    sx={{
                      "& > div": {
                        gridColumn: isNonMobile ? undefined : "span 4",
                      },
                    }}
                  >
                    {/* File dropzone */}
                    <Box sx={{ gridColumn: "span 2" }}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textTransform: "none",
                          border: `1.5px dashed ${
                            touched.file && errors.file
                              ? "#e06666"
                              : BRAND_BLUE_BRIGHT
                          }`,
                          color: BRAND_BLUE_BRIGHT,
                          bgcolor: isDark
                            ? "rgba(46,155,230,0.06)"
                            : "rgba(46,155,230,0.05)",
                          flexDirection: "column",
                          gap: 0.75,
                          "&:hover": {
                            border: `1.5px dashed ${NEON_GREEN}`,
                            bgcolor: `${NEON_GREEN}12`,
                            boxShadow: `0 0 14px ${NEON_GREEN}33`,
                          },
                        }}
                      >
                        <CloudUploadOutlinedIcon />
                        <Typography variant="body2" fontWeight={600}>
                          {values.file
                            ? "Replace statement"
                            : "Select a statement to upload"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF / CSV / XLS — max 50MB
                        </Typography>
                        <input
                          id="file"
                          name="file"
                          type="file"
                          hidden
                          onChange={(event) => {
                            setFieldValue("file", event.currentTarget.files[0]);
                          }}
                        />
                      </Button>
                      {touched.file && errors.file && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "flex", gap: 0.5 }}
                        >
                          <ErrorOutlineIcon sx={{ fontSize: 13 }} />
                          {errors.file}
                        </Typography>
                      )}
                      {values.file && !errors.file && (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          mt={0.75}
                          sx={{ color: NEON_GREEN }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                          <Typography variant="caption" noWrap>
                            {values.file.name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <FuturisticTextField
                      fullWidth
                      variant="filled"
                      type="password"
                      label="Statement Password *"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.password || ""}
                      name="password"
                      error={touched.password && !!errors.password}
                      helperText={touched.password && errors.password}
                      sx={{ gridColumn: "span 2" }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="end" mt="20px">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={15} color="inherit" />
                        ) : (
                          <CloudUploadOutlinedIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        px: 3,
                        color: "#0a3a5c",
                        background: NEON_GREEN,
                        boxShadow: "0 0 16px rgba(122,217,170,0.45)",
                        "&:hover": {
                          background: "#96e6bd",
                          boxShadow: "0 0 24px rgba(122,217,170,0.65)",
                        },
                        "&.Mui-disabled": {
                          background: isDark ? "#3a4a58" : "#c9d4dd",
                          color: isDark ? "#7b8b99" : "#8fa0ad",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isSubmitting ? "Crunching..." : "Submit for Crunching"}
                    </Button>
                  </Box>
                </CardContent>
              </GlassCard>
            </form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default UploadFinsightsStatementForm;